/**
 * Floating Quick Calm trigger button. Draggable, with a hover popover that
 * offers "hide" and a drag handle. Pure presentational — owns no breath/audio
 * state, just the button and its persisted position.
 */
import React, { useRef, useState } from 'react';
import { motion, useDragControls } from 'framer-motion';
import { Wind, GripVertical, EyeOff } from 'lucide-react';
import { SCALE_ANIMATE } from '../../animations';

const POS_KEY = 'neuroheal_quickcalm_position';
const HIDE_KEY = 'neuroheal_quickcalm_hidden';

const loadInitialPosition = () => {
  try {
    const saved = localStorage.getItem(POS_KEY);
    return saved ? JSON.parse(saved) : { x: 0, y: 0 };
  } catch {
    return { x: 0, y: 0 };
  }
};

const FloatingTrigger = ({ onOpen }) => {
  const [position, setPosition] = useState(loadInitialPosition);
  const dragControls = useDragControls();
  const constraintsRef = useRef(null);

  const savePosition = (x, y) => {
    const newPos = { x, y };
    setPosition(newPos);
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(newPos));
    } catch { /* localStorage may be unavailable */ }
  };

  const hideButton = () => {
    try { localStorage.setItem(HIDE_KEY, 'true'); } catch { /* noop */ }
    window.location.reload();
  };

  return (
    <>
      <div ref={constraintsRef} className="fixed inset-0 pointer-events-none z-40" />

      <motion.div
        drag
        dragControls={dragControls}
        dragMomentum={false}
        dragElastic={0.1}
        dragConstraints={constraintsRef}
        onDragEnd={(_e, info) => savePosition(info.point.x, info.point.y)}
        initial={{ opacity: 0, scale: 0 }}
        animate={SCALE_ANIMATE}
        transition={{ delay: 1 }}
        className="fixed bottom-24 right-6 z-40 group"
        style={{ x: position.x, y: position.y }}
      >
        <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto">
          <div className="bg-slate-900/90 backdrop-blur-xl rounded-xl p-2 flex items-center gap-2 border border-white/10">
            <button
              onClick={hideButton}
              className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-all"
              title="Hide Quick Calm"
              data-testid="quick-calm-hide"
            >
              <EyeOff className="w-4 h-4" />
            </button>
            <div className="w-px h-6 bg-white/10" />
            <div className="p-2 text-white/40 cursor-grab active:cursor-grabbing" title="Drag to move">
              <GripVertical className="w-4 h-4" />
            </div>
          </div>
        </div>

        <button
          onClick={onOpen}
          className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 text-white shadow-lg shadow-purple-500/30 flex items-center justify-center hover:scale-110 transition-transform"
          data-testid="quick-calm-button"
          title="Quick Calm - 60 second reset"
        >
          <Wind className="w-6 h-6" />
        </button>
      </motion.div>
    </>
  );
};

export default FloatingTrigger;
